import { createUseStyles } from 'react-jss';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BackgroundColors } from '@picsart/design-system/foundation/colors';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { BREAKPOINTS } from '@/consts/breakpoints';

/**
 * Radius sits on the frame, which clips the track — the slides themselves are
 * square so nothing rounds mid-scroll. Mirrors `Card`'s responsive radius so a
 * carousel and a plain tile sit in the same grid without a visible seam.
 */
const useStyles = createUseStyles(
  {
    frame: {
      position: 'relative',
      width: '100%',
      aspectRatio: '1 / 1',
      borderRadius: CornerRadius.r12,
      overflow: 'hidden',
      backgroundColor: BackgroundColors.base,
      [`@media (max-width: ${Breakpoints.xs - 1}px)`]: {
        borderRadius: CornerRadius.r8,
      },
      // BREAKPOINTS.narrow (280px) has no DS equivalent — matches Card, where
      // r8 looks cramped on ultra-narrow screens and r12 reads cleaner.
      [`@media (max-width: ${BREAKPOINTS.narrow - 1}px)`]: {
        borderRadius: CornerRadius.r12,
      },
    },
    // Reset the button chrome the frame inherits when it is clickable.
    interactive: {
      padding: 0,
      border: 'none',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      display: 'block',
      textAlign: 'inherit',
    },
    frameFill: {
      position: 'absolute',
      inset: 0,
      width: 'auto',
      height: 'auto',
      aspectRatio: 'auto',
    },
    /**
     * One row of full-width slides moved by transform, so the browser animates
     * it on the compositor. `scroll-behavior`/`scrollLeft` would fight the
     * user's own scroll on a touch grid.
     */
    track: {
      display: 'flex',
      width: '100%',
      height: '100%',
      transition: `transform 0.45s ${Easings.easeOut}`,
    },
    /**
     * Reduced-motion mode. The slides stop travelling and stack instead, the
     * outgoing one cross-fading into the next.
     *
     * A fade is the standard substitute: what the setting asks for is the
     * absence of *movement* — travel, direction, parallax — not the absence of
     * change, and an opacity ramp has none of it. Cutting hard was the other
     * option and reads as a glitch at this cadence, four frames snapping over
     * eight seconds with nothing to connect them.
     */
    trackFade: {
      display: 'block',
      position: 'relative',
      transition: 'none',
    },
    slide: {
      flex: '0 0 100%',
      width: '100%',
      height: '100%',
      position: 'relative',
    },
    /**
     * Stacked rather than laid out in a row, so the two frames of a crossfade
     * occupy the same box.
     *
     * The opacity ramp is deliberate and stays even under reduced motion —
     * `transition: none` here is a rejected fix, not an oversight. The carousel
     * advances on a timer with no control to stop it, so removing the
     * transition does not remove the animation, it only makes the change
     * instant: slides snap every `CAROUSEL_INTERVAL_MS`, which reads as
     * flashing rather than as calm. `prefers-reduced-motion` is about movement
     * — travel, parallax, zoom, the things that provoke a vestibular response —
     * and a cross-fade has none, which is why it is the substitution the
     * guidance recommends. The travel is what `trackFade` already removes.
     */
    slideFade: {
      position: 'absolute',
      inset: 0,
      opacity: 0,
      transition: `opacity 0.6s ${Easings.easeInOut}`,
    },
    slideFadeVisible: {
      opacity: 1,
    },
    media: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    // Declared after `media` in the same sheet, so it wins on order without any
    // specificity games. See the `fit` prop.
    mediaContain: {
      objectFit: 'contain',
    },
  },
  { name: 'media-carousel' }
);

export default useStyles;
